import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, Grid, Card, CardContent, Button, useTheme, AppBar, Toolbar, Container, Avatar, Tooltip, Menu, MenuItem, Tabs, Tab } from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import TranslateIcon from '@mui/icons-material/Translate';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SecurityIcon from '@mui/icons-material/Security';
import SpeedIcon from '@mui/icons-material/Speed';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import PersonIcon from '@mui/icons-material/Person';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import AppFooter from '../components/Layout/AppFooter';
import LoginModal from '../components/Common/LoginModal';
import InviteCodeModal from '../components/Common/InviteCodeModal';
import { logout } from '../store/actions/authActions';
import { checkInviteEligibility } from '../store/actions/inviteActions';

const Landing = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { user, isAuthenticated } = useSelector((state) => state.auth);
  const { eligible } = useSelector((state) => state.invite);
  
  // 滚动状态
  const [scrollY, setScrollY] = useState(0);
  const [showNavBackground, setShowNavBackground] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  
  // 登录弹窗状态
  const [showLoginModal, setShowLoginModal] = useState(false);
  
  // 邀请码弹窗状态
  const [showInviteModal, setShowInviteModal] = useState(false);
  
  // 3D动画相关状态
  const documentsContainerRef = useRef(null);
  const particlesRef = useRef(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [currentRotation, setCurrentRotation] = useState({ x: 0, y: 0 });
  
  // 进入动画状态
  const [titleVisible, setTitleVisible] = useState(false);
  const [documentsVisible, setDocumentsVisible] = useState([false, false, false, false, false]);
  const titleRef = useRef(null);
  const documentRefs = useRef([]);
  
  // 导航菜单项
  const navigationItems = [
    { label: '首页', path: '/landing' },
    { label: '证件翻译', path: '/trans' },          
    { label: '认证', path: '/certification' },
    { label: '服务', path: '/services' },
    { label: '充值', path: '/pricing' },
    { label: '安全', path: '/security' }
  ];

  // 检查邀请码资格
  useEffect(() => {
    if (isAuthenticated && user) {
      dispatch(checkInviteEligibility()).catch(error => {
        //console.error('检查邀请码资格失败:', error);
      });
    }
  }, [isAuthenticated, user, dispatch]);

  // 功能特性数据
  const features = [
    {
      icon: <TranslateIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
      title: '专业翻译',
      description: '支持图片、PDF、Word等多种文档格式的专业翻译服务，确保翻译质量和准确性'
    },
    {
      icon: <CloudUploadIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
      title: '便捷上传',
      description: '支持拖拽上传，批量处理，让文档翻译变得简单高效'
    },
    {
      icon: <SecurityIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
      title: '安全保障',
      description: '企业级安全保护，确保您的文档信息安全可靠'
    },
    {
      icon: <SpeedIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />,
      title: '人工审核',
      description: '智能翻译引擎，人工专业翻译审核，确保翻译质量和准确性'
    }
  ];

  // 滚动监听
  useEffect(() => {
    const handleScroll = () => {
      const currentScrollY = window.scrollY;
      setScrollY(currentScrollY);
      setShowNavBackground(currentScrollY > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // 鼠标移动监听（3D效果）
  useEffect(() => {
    const handleMouseMove = (e) => {
      const mouseX = (e.clientX / window.innerWidth) * 2 - 1;
      const mouseY = (e.clientY / window.innerHeight) * 2 - 1;
      
      setMousePosition({ x: mouseX * 5, y: -mouseY * 3 });
    };

    document.addEventListener('mousemove', handleMouseMove);
    return () => document.removeEventListener('mousemove', handleMouseMove);
  }, []);

  // 3D旋转动画
  useEffect(() => {
    const animate = () => {
      setCurrentRotation(prev => ({
        x: prev.x + (mousePosition.x - prev.x) * 0.03,
        y: prev.y + (mousePosition.y - prev.y) * 0.03
      }));
      requestAnimationFrame(animate);
    };
    
    const animationId = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(animationId);
  }, [mousePosition]);

  // 创建背景粒子
  useEffect(() => {
    if (!particlesRef.current) return;
    
    const particlesContainer = particlesRef.current;
    particlesContainer.innerHTML = '';
    
    for (let i = 0; i < 50; i++) {
      const particle = document.createElement('div');
      particle.style.cssText = `
        position: absolute;
        width: 2px;
        height: 2px;
        background: rgba(67, 83, 255, 0.3);
        border-radius: 50%;
        left: ${Math.random() * 100}%;
        top: ${Math.random() * 100}%;
        animation: float ${Math.random() * 4 + 4}s infinite ease-in-out;
        animation-delay: ${Math.random() * 6}s;
      `;
      particlesContainer.appendChild(particle);
    }
  }, []);

  // 进入动画初始化
  useEffect(() => {
    // 延迟500ms后显示标题
    const titleTimer = setTimeout(() => {
      setTitleVisible(true);
    }, 500);

    // 延迟1000ms后依次显示文档，每个文档间隔150ms
    const documentTimers = [];
    documentsVisible.forEach((_, index) => {
      const timer = setTimeout(() => {
        setDocumentsVisible(prev => {
          const newState = [...prev];
          newState[index] = true;
          return newState;
        });
      }, 1000 + index * 150);
      documentTimers.push(timer);
    });

    return () => {
      clearTimeout(titleTimer);
      documentTimers.forEach(timer => clearTimeout(timer));
    };
  }, []);

  // 导航栏相关函数
  const getCurrentTab = () => {
    const currentPath = location.pathname;
    const index = navigationItems.findIndex(item => item.path === currentPath);
    return index >= 0 ? index : 0;
  };

  const handleTabChange = (event, newValue) => {
    navigate(navigationItems[newValue].path);
    // 滚动到页面顶部
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleOpenMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
  };

  const handleProfile = () => {
    handleCloseMenu();
    navigate('/profile');
  };

  // 处理填写邀请码
  const handleInviteCode = () => {
    handleCloseMenu();
    setShowInviteModal(true);  
  };

  const handleLogout = () => {
    handleCloseMenu();
    dispatch(logout());
    navigate('/landing');
  };

  // 处理登录按钮点击
  const handleLoginClick = () => {
    setShowLoginModal(true);
  };

  // 处理登录成功
  const handleLoginSuccess = (userData) => {
    //console.log('登录成功，用户数据:', userData);
    setShowLoginModal(false);
    // 跳转到翻译页面
    navigate('/trans');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // 处理邀请码验证成功
  const handleInviteSuccess = (result) => {
    //console.log('邀请码验证成功:', result);
    // 可以在这里显示成功通知或刷新用户信息
  };

  // 计算导航元素透明度（除了Logo）
  const getNavElementOpacity = () => {
    // 如果不在Landing页面，返回100%透明度
    if (location.pathname !== '/landing') {
      return 1;
    }
    
    // 在Landing页面，根据滚动位置计算透明度
    // 首屏时30%，滚动到100px时达到100%
    const baseOpacity = 0.4;
    const maxOpacity = 1;
    const scrollThreshold = 100;
    
    if (scrollY <= 0) {
      return baseOpacity;
    } else if (scrollY >= scrollThreshold) {
      return maxOpacity;
    } else {
      // 线性插值
      return baseOpacity + (maxOpacity - baseOpacity) * (scrollY / scrollThreshold);
    }
  };
  
  const navElementOpacity = getNavElementOpacity();

  return (
    <Box sx={{ position: 'relative', minHeight: '100vh' }}>
      {/* 动态样式 */}
      <style>
        {`
          @keyframes float {
            0%, 100% { transform: translateY(0px) rotate(0deg); opacity: 0; }
            50% { transform: translateY(-100px) rotate(180deg); opacity: 1; }
          }
          
          @keyframes scan {
            0% { transform: translateY(-10px); opacity: 0; }
            50% { opacity: 1; }
            100% { transform: translateY(310px); opacity: 0; }
          }
        `}
      </style>

      {/* 自定义导航栏 */}
      <AppBar 
        position="fixed" 
        elevation={0}
        sx={{
          background: showNavBackground 
            ? 'white' 
            : 'transparent',
          backdropFilter: showNavBackground ? 'none' : 'blur(10px)',
          transition: 'all 0.3s ease',
          borderBottom: 'none',
          py: 0.5,
          padding: 0,
          boxShadow: showNavBackground 
            ? '0 2px 10px rgba(0, 0, 0, 0.05)' 
            : 'none',
          color: showNavBackground 
            ? theme.palette.primary.main 
            : 'white'
        }}
      >
        <Container maxWidth="xl">
          <Toolbar disableGutters sx={{ justifyContent: 'space-between' }}>
            {/* Logo */}
            <Box
              sx={{ 
                display: 'flex',
                alignItems: 'center',
                cursor: 'pointer'
              }}
              onClick={() => {
                navigate('/landing');
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              {/* SVG Logo - 提取的流畅曲线 */}
              <svg
                width="36"
                height="24"
                viewBox="0 0 1024 300"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M566.784 221.76c-2.048 4.096-4.096 7.68-5.632 11.776-15.36 23.04-27.648 47.616-36.864 72.704-12.8 36.352-5.632 68.608 31.232 81.408 66.048 20.48 137.216 17.408 201.216-8.704 16.896-5.632 21.504-8.192 64.512-24.576 31.232-12.8 62.976-24.064 95.744-32.256 2.048-0.512 4.096-1.024 5.632-2.048 18.432-3.584 12.8-10.752 31.744 2.048 7.168 5.632 16.384 7.168 25.088 5.632 8.704-2.048 10.24-5.632 15.872-14.336 10.24-15.36 6.144-36.352-8.704-47.104-35.84-24.576-42.496-18.944-77.824-11.776-2.56 1.024-5.632 1.536-7.68 2.048-35.84 9.216-71.168 20.992-104.448 35.328-40.96 16.384-45.568 17.92-61.44 23.552-46.592 19.456-97.792 23.552-147.456 11.776 8.704-20.992 18.944-40.96 31.232-60.416 2.56-3.584 4.608-8.192 7.168-11.776 16.384-26.112 28.16-54.272 34.816-84.48 8.192-36.352-14.848-72.192-50.688-80.384-1.536-0.512-2.56-0.512-4.096-1.024-51.712-12.8-112.128 5.632-280.576 71.168-154.112 60.416-221.696 79.36-243.2 67.072-7.68-4.608-16.384-5.632-25.088-2.56-17.92 5.12-28.672 24.064-23.552 41.472 2.56 8.192 7.68 15.872 15.872 19.968 43.52 16.384 92.16 14.848 134.656-3.584 56.32-16.896 112.128-37.376 165.888-60.416 146.432-57.856 208.896-76.288 239.104-68.608 5.632 2.048 5.632 0 4.608 5.632-5.632 22.528-14.336 43.52-27.136 62.464z"
                  fill="currentColor"
                  stroke="currentColor"
                  strokeWidth="8"
                  strokeLinejoin="round"
                />
              </svg>
            </Box>
            
            {/* 右侧区域：导航菜单 + 用户信息 */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {/* 导航菜单 */}
              <Tabs
                value={getCurrentTab()}
                onChange={handleTabChange}
                sx={{
                  opacity: navElementOpacity,
                  transition: 'opacity 0.3s ease',
                  '& .MuiTabs-indicator': {
                    backgroundColor: showNavBackground 
                      ? theme.palette.primary.main 
                      : 'white',
                    height: 2,
                    borderRadius: '5px 5px 0 0',
                    boxShadow: showNavBackground 
                      ? '0 2px 8px rgba(67, 83, 255, 0.4)' 
                      : '0 2px 8px rgba(255, 255, 255, 0.4)',
                  },
                  '& .MuiTab-root': {
                    textTransform: 'none',
                    fontWeight: 500,
                    fontSize: '1rem',
                    color: showNavBackground 
                      ? theme.palette.text.secondary 
                      : 'rgba(255, 255, 255, 0.9)',
                    minWidth: 80,
                    minHeight: 64,
                    padding: '12px 12px',
                    transition: 'color 0.3s ease',
                    '&.Mui-selected': {
                      color: showNavBackground 
                        ? theme.palette.primary.main 
                        : 'rgba(255, 255, 255, 1)',
                      fontWeight: 600
                    },
                    '&:hover': {
                      color: showNavBackground 
                        ? theme.palette.primary.main 
                        : 'rgba(255, 255, 255, 1)',
                      opacity: 0.8
                    }
                  }
                }}
              >
                {navigationItems.map((item, index) => (
                  <Tab
                    key={index}
                    label={item.label}
                    sx={{ mx: 0.5 }}
                  />
                ))}
              </Tabs>
              
              {/* 用户信息 */}
              {isAuthenticated ? (
                <Box sx={{ display: 'flex', alignItems: 'center', opacity: navElementOpacity, transition: 'opacity 0.3s ease', paddingLeft: '20px' }}>
                  <Box 
                    sx={{ 
                      backgroundColor: showNavBackground 
                        ? 'rgb(245, 245, 245)' 
                        : 'rgba(255, 255, 255, 0.08)',
                      px: 2, 
                      py: 0.5, 
                      borderRadius: 4,
                      mr: 2,
                      display: { xs: 'none', sm: 'flex' },
                      transition: 'background-color 0.3s ease'
                    }}
                  >
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        color: showNavBackground 
                          ? theme.palette.primary.main 
                          : 'rgba(255, 255, 255, 0.9)', 
                        fontWeight: 'medium',
                        transition: 'color 0.3s ease'
                      }}
                    >
                      {user.points} 点数
                    </Typography>
                  </Box>
                  
                  <Tooltip title="账户设置">
                    <Button
                      onClick={handleOpenMenu}
                      endIcon={<ArrowDropDownIcon sx={{ 
                        color: showNavBackground 
                          ? theme.palette.primary.main 
                          : 'rgba(255, 255, 255, 0.9)',
                        transition: 'color 0.3s ease'
                      }} />}
                      sx={{ 
                        textTransform: 'none',
                        color: showNavBackground 
                          ? theme.palette.primary.main 
                          : 'rgba(255, 255, 255, 0.9)',
                        transition: 'color 0.3s ease',
                        '&:hover': {
                          backgroundColor: showNavBackground 
                            ? theme.palette.action.hover 
                            : 'rgba(255, 255, 255, 0.08)',
                        },
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <Avatar 
                          alt={user.username}
                          src={user.avatar || ''}
                          sx={{ 
                            width: 32, 
                            height: 32, 
                            mr: 1,
                            bgcolor: showNavBackground 
                              ? theme.palette.primary.main 
                              : 'rgba(255, 255, 255, 0.15)',
                            color: 'white',
                            transition: 'background-color 0.3s ease'
                          }}
                        >
                          {user.username.charAt(0).toUpperCase()}
                        </Avatar>
                        <Typography 
                          variant="subtitle2" 
                          sx={{ 
                            display: { xs: 'none', md: 'block' }, 
                            color: showNavBackground 
                              ? theme.palette.primary.main 
                              : 'rgba(255, 255, 255, 0.9)',
                            transition: 'color 0.3s ease'
                          }}
                        >
                          {user.username}
                        </Typography>
                      </Box>
                    </Button>
                  </Tooltip>
                  
                  <Menu
                    anchorEl={anchorEl}
                    id="account-menu"
                    open={Boolean(anchorEl)}
                    onClose={handleCloseMenu}
                    PaperProps={{
                      elevation: 2,
                      sx: {
                        overflow: 'visible',
                        filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.08))',
                        mt: 1.5,
                        borderRadius: 2,
                        minWidth: 180,
                        '& .MuiMenuItem-root': {
                          px: 2,
                          py: 1.5,
                          borderRadius: 1,
                          mx: 1,
                          my: 0.5,
                          '&:hover': {
                            backgroundColor: theme.palette.background.light,
                          },
                        },
                      },
                    }}
                    transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                    anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                  >
                    <MenuItem onClick={handleProfile}>
                      <PersonIcon sx={{ mr: 1, fontSize: 20 }} />
                      个人中心
                    </MenuItem>
                    {eligible && (
                      <MenuItem onClick={handleInviteCode}>
                        <CardGiftcardIcon sx={{ mr: 1, fontSize: 20 }} />
                        填写邀请码
                      </MenuItem>
                    )}
                    <MenuItem onClick={handleLogout}>
                      <LogoutIcon sx={{ mr: 1, fontSize: 20 }} />
                      退出登录
                    </MenuItem>
                  </Menu>
                </Box>
              ) : (
                <Box sx={{ paddingLeft: '20px' }}>
                  <Button
                    variant="contained"
                    startIcon={<LoginIcon />}
                    onClick={handleLoginClick}
                    sx={{
                      textTransform: 'none',
                      fontWeight: 600,
                      px: 2.5,
                      py: 0.75,
                      borderRadius: 3,
                      // 在Landing页面默认使用hover状态的样式
                      backgroundColor: showNavBackground 
                        ? theme.palette.primary.main 
                        : 'rgba(255, 255, 255, 0.08)',
                      color: showNavBackground 
                        ? 'white' 
                        : 'rgba(255, 255, 255, 0.9)',
                      boxShadow: showNavBackground 
                        ? '0 4px 12px rgba(67, 83, 255, 0.3)' 
                        : '0 4px 12px rgba(255, 255, 255, 0.1)',
                      border: showNavBackground 
                        ? 'none' 
                        : '1px solid rgba(255, 255, 255, 0.2)',
                      '&:hover': {
                        backgroundColor: showNavBackground 
                          ? theme.palette.primary.main 
                          : 'rgba(255, 255, 255, 0.15)',
                        boxShadow: showNavBackground 
                          ? '0 6px 16px rgba(67, 83, 255, 0.4)' 
                          : '0 6px 16px rgba(255, 255, 255, 0.2)',
                        transform: 'translateY(-1px)',
                      },
                      transition: 'all 0.2s ease'
                    }}
                  >
                    立即登录体验
                  </Button>
                </Box>
              )}
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {/* 首屏 - 3D动画区域 */}
      <Box
        sx={{
          height: '100vh',
          overflow: 'hidden',
          background: 'linear-gradient(135deg, #0a1428 0%, #1a2744 50%, #0a1428 100%)',
          position: 'relative',
          perspective: '1000px'
        }}
      >
        {/* 背景粒子 */}
        <Box
          ref={particlesRef}
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 0
          }}
        />
        
        {/* 半透明蒙层 */}
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            background: 'rgba(0, 20, 40, 0.85)',
            backgroundImage: `
              radial-gradient(circle at 20% 20%, rgba(67, 83, 255, 0.1) 0%, transparent 50%),
              radial-gradient(circle at 80% 80%, rgba(67, 83, 255, 0.05) 0%, transparent 50%)
            `,
            zIndex: 1
          }}
        />

        {/* 主标题区域 */}
        <Box
          ref={titleRef}
          sx={{
            position: 'fixed',
            top: '15%',
            left: '50%',
            transform: titleVisible 
              ? 'translateX(-50%) translateY(0)' 
              : 'translateX(-50%) translateY(-50px)',
            opacity: titleVisible ? 1 : 0,
            zIndex: 10,
            textAlign: 'center',
            color: 'white',
            transition: 'all 1s ease'
          }}
        >
          <Typography
            variant="h1"
            sx={{
              fontSize: '7rem',
              fontWeight: 700,
              background: 'linear-gradient(135deg, #4353ff, #6b78ff)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              mb: 2,
              textShadow: '0 0 30px rgba(67, 83, 255, 0.3)'
            }}
          >
            PageSmooth
          </Typography>
          <Typography
            variant="h4"
            sx={{
              fontSize: '1.5rem',
              color: 'rgba(255, 255, 255, 0.8)',
              fontWeight: 300,
              mb: 4
            }}
          >
             所有证件丝滑翻译，多语种，人工审核，安全可靠
          </Typography>
          <Button
            variant="contained"
            size="large"
            onClick={() => {
              navigate('/trans');
              window.scrollTo({ top: 0, behavior: 'smooth' });
            }}
            sx={{
              background: 'linear-gradient(135deg, #4353ff, #6b78ff)',
              color: 'white',
              border: 'none',
              px: 5,
              py: 2,
              fontSize: '1.1rem',
              borderRadius: '50px',
              boxShadow: '0 10px 30px rgba(67, 83, 255, 0.3)',
              '&:hover': {
                transform: 'translateY(-3px)',
                boxShadow: '0 15px 40px rgba(67, 83, 255, 0.4)'
              },
              transition: 'all 0.3s ease'
            }}
          >
            开始体验
          </Button>
        </Box>

        {/* 3D文档容器 */}
        <Box
          ref={documentsContainerRef}
          sx={{
            position: 'fixed',
            top: '50%',
            left: '50%',
            transform: `translate(-50%, -50%) rotateX(${currentRotation.y}deg) rotateY(${currentRotation.x}deg)`,
            width: '100vw',
            height: '100vh',
            maxWidth: '1200px',
            maxHeight: '800px',
            transformStyle: 'preserve-3d',
            zIndex: 2,
            pointerEvents: 'none',
            perspective: '1500px',
            transition: 'transform 0.1s ease-out'
          }}
        >
          {/* 古代羊皮纸卷轴 */}
          <Box
            ref={el => documentRefs.current[0] = el}
            sx={{
              position: 'absolute',
              width: '280px',
              height: '200px',
              borderRadius: '12px',
              background: 'linear-gradient(45deg, #f4e4bc, #e6d3a3)',
              backgroundImage: `
                radial-gradient(circle at 30% 40%, rgba(139, 69, 19, 0.1) 0%, transparent 50%),
                linear-gradient(90deg, transparent 0%, rgba(139, 69, 19, 0.05) 50%, transparent 100%)
              `,
              transform: documentsVisible[0]
                ? 'translateX(-480px) translateY(130px) translateZ(0px) rotateX(20deg) rotateY(-20deg) rotateZ(12deg)'
                : 'translateX(0px) translateY(0px) translateZ(0px) rotateX(0deg) rotateY(0deg) rotateZ(0deg)',
              opacity: documentsVisible[0] ? 0.8 : 0,
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
              transition: 'all 1s cubic-bezier(0.4, 0, 0.2, 1)',
              cursor: 'pointer',
              overflow: 'hidden',
              top: '60%',
              left: '50%',
              marginTop: '-100px',
              marginLeft: '-140px',
              pointerEvents: 'auto',
              '&:hover': {
                transform: 'translateX(-480px) translateY(110px) translateZ(0px) rotateX(20deg) rotateY(-20deg) rotateZ(12deg) scale(1.05)',
                boxShadow: '0 40px 80px rgba(67, 83, 255, 0.3)',
                zIndex: 100
              }
            }}
          >
            <Box sx={{ p: 2.5, height: '100%', position: 'relative', overflow: 'hidden' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1.5, color: '#8b4513', fontFamily: 'serif', fontSize: '1rem' }}>
                古代诗词卷轴
              </Typography>
              <Typography variant="body2" sx={{ lineHeight: 1.4, color: '#8b4513', fontFamily: 'serif', fontSize: '0.8rem' }}>
                床前明月光，疑是地上霜。<br />
                举头望明月，低头思故乡。<br /><br />
                <span style={{ opacity: 0.6 }}>→ 正在翻译为英文...</span>
              </Typography>
            </Box>
          </Box>

          {/* 中世纪手抄本 */}
          <Box
            ref={el => documentRefs.current[1] = el}
            sx={{
              position: 'absolute',
              width: '280px',
              height: '200px',
              borderRadius: '12px',
              background: 'linear-gradient(45deg, #f5f1e8, #ede4d3)',
              border: '3px solid #8b4513',
              transform: documentsVisible[1]
                ? 'translateX(-240px) translateY(100px) translateZ(0px) rotateX(10deg) rotateY(-10deg) rotateZ(6deg)'
                : 'translateX(0px) translateY(0px) translateZ(0px) rotateX(0deg) rotateY(0deg) rotateZ(0deg)',
              opacity: documentsVisible[1] ? 0.85 : 0,
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
              transition: 'all 1s cubic-bezier(0.4, 0, 0.2, 1)',
              cursor: 'pointer',
              overflow: 'hidden',
              top: '60%',
              left: '50%',
              marginTop: '-100px',
              marginLeft: '-140px',
              pointerEvents: 'auto',
              '&:hover': {
                transform: 'translateX(-240px) translateY(80px) translateZ(0px) rotateX(10deg) rotateY(-10deg) rotateZ(6deg) scale(1.05)',
                boxShadow: '0 40px 80px rgba(67, 83, 255, 0.3)',
                zIndex: 100
              }
            }}
          >
            <Box sx={{ p: 2.5, height: '100%', position: 'relative', overflow: 'hidden' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1.5, color: '#654321', fontFamily: 'serif', fontSize: '1rem' }}>
                Medieval Manuscript
              </Typography>
              <Typography variant="body2" sx={{ lineHeight: 1.4, color: '#654321', fontFamily: 'serif', fontSize: '0.8rem' }}>
                Lorem ipsum dolor sit amet,<br />
                consectetur adipiscing elit.<br />
                Sed do eiusmod tempor incididunt<br />
                ut labore et dolore magna aliqua.<br /><br />
                <span style={{ opacity: 0.6 }}>→ 翻译手抄本...</span>
              </Typography>
            </Box>
          </Box>

          {/* 成绩单（中心） */}
          <Box
            ref={el => documentRefs.current[2] = el}
            sx={{
              position: 'absolute',
              width: '280px',
              height: '200px',
              borderRadius: '12px',
              background: 'white',
              border: '1px solid #ddd',
              transform: documentsVisible[2]
                ? 'translateX(0px) translateY(55px) translateZ(0px) rotateX(0deg) rotateY(0deg) rotateZ(0deg)'
                : 'translateX(0px) translateY(0px) translateZ(0px) rotateX(0deg) rotateY(0deg) rotateZ(0deg)',
              opacity: documentsVisible[2] ? 1 : 0,
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
              transition: 'all 1s cubic-bezier(0.4, 0, 0.2, 1)',
              cursor: 'pointer',
              overflow: 'hidden',
              top: '60%',
              left: '50%',
              marginTop: '-100px',
              marginLeft: '-140px',
              pointerEvents: 'auto',
              zIndex: 10,
              '&:hover': {
                transform: 'translateX(0px) translateY(35px) translateZ(0px) rotateX(0deg) rotateY(0deg) rotateZ(0deg) scale(1.05)',
                boxShadow: '0 40px 80px rgba(67, 83, 255, 0.3)',
                zIndex: 100
              }
            }}
          >
            <Box sx={{ p: 2.5, height: '100%', position: 'relative', overflow: 'hidden' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1.5, color: '#333', fontSize: '1rem' }}>
                Academic Transcript
              </Typography>
              <Typography variant="body2" sx={{ lineHeight: 1.4, color: '#666', fontSize: '0.8rem' }}>
                <strong>Student Name:</strong> John Smith<br />
                <strong>Mathematics:</strong> A+<br />
                <strong>Physics:</strong> A<br />
                <strong>Chemistry:</strong> A-<br />
                <strong>GPA:</strong> 3.85<br /><br />
                <span style={{ color: '#4353ff' }}>→ 翻译成绩单</span>
              </Typography>
            </Box>
          </Box>

          {/* 银行流水 */}
          <Box
            ref={el => documentRefs.current[3] = el}
            sx={{
              position: 'absolute',
              width: '280px',
              height: '200px',
              borderRadius: '12px',
              background: 'linear-gradient(to bottom, #ffffff, #f8f9fa)',
              border: '1px solid #e9ecef',
              transform: documentsVisible[3]
                ? 'translateX(240px) translateY(100px) translateZ(0px) rotateX(10deg) rotateY(-10deg) rotateZ(-6deg)'
                : 'translateX(0px) translateY(0px) translateZ(0px) rotateX(0deg) rotateY(0deg) rotateZ(0deg)',
              opacity: documentsVisible[3] ? 0.85 : 0,
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
              transition: 'all 1s cubic-bezier(0.4, 0, 0.2, 1)',
              cursor: 'pointer',
              overflow: 'hidden',
              top: '60%',
              left: '50%',
              marginTop: '-100px',
              marginLeft: '-140px',
              pointerEvents: 'auto',
              '&:hover': {
                transform: 'translateX(240px) translateY(80px) translateZ(0px) rotateX(10deg) rotateY(-10deg) rotateZ(-6deg) scale(1.05)',
                boxShadow: '0 40px 80px rgba(67, 83, 255, 0.3)',
                zIndex: 100
              }
            }}
          >
            <Box sx={{ p: 2.5, height: '100%', position: 'relative', overflow: 'hidden' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1.5, color: '#333', fontSize: '1rem' }}>
                Bank Statement
              </Typography>
              <Typography variant="body2" sx={{ lineHeight: 1.4, color: '#666', fontSize: '0.8rem' }}>
                <strong>Account:</strong> ****1234<br />
                <strong>Date:</strong> 2024-01-15<br />
                <strong>Transaction:</strong> Transfer<br />
                <strong>Amount:</strong> $1,500.00<br />
                <strong>Balance:</strong> $5,230.50<br /><br />
                <span style={{ color: '#4353ff' }}>→ 翻译银行流水</span>
              </Typography>
            </Box>
          </Box>

          {/* 科幻证件 */}
          <Box
            ref={el => documentRefs.current[4] = el}
            sx={{
              position: 'absolute',
              width: '280px',
              height: '200px',
              borderRadius: '12px',
              background: 'linear-gradient(135deg, #1a1a2e, #16213e)',
              border: '2px solid #4353ff',
              transform: documentsVisible[4]
                ? 'translateX(480px) translateY(130px) translateZ(0px) rotateX(20deg) rotateY(-20deg) rotateZ(-12deg)'
                : 'translateX(0px) translateY(0px) translateZ(0px) rotateX(0deg) rotateY(0deg) rotateZ(0deg)',
              opacity: documentsVisible[4] ? 0.8 : 0,
              boxShadow: `
                0 0 20px rgba(67, 83, 255, 0.5),
                inset 0 0 20px rgba(67, 83, 255, 0.1),
                0 20px 40px rgba(0, 0, 0, 0.3)
              `,
              transition: 'all 1s cubic-bezier(0.4, 0, 0.2, 1)',
              cursor: 'pointer',
              overflow: 'hidden',
              top: '60%',
              left: '50%',
              marginTop: '-100px',
              marginLeft: '-140px',
              pointerEvents: 'auto',
              '&:hover': {
                transform: 'translateX(480px) translateY(110px) translateZ(0px) rotateX(20deg) rotateY(-20deg) rotateZ(-12deg) scale(1.05)',
                boxShadow: '0 40px 80px rgba(67, 83, 255, 0.3)',
                zIndex: 100
              }
            }}
          >
            <Box sx={{ p: 2.5, height: '100%', position: 'relative', overflow: 'hidden' }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1.5, color: 'white', textShadow: '0 0 10px rgba(67, 83, 255, 0.5)', fontSize: '1rem' }}>
                DIGITAL ID CARD
              </Typography>
              <Typography variant="body2" sx={{ lineHeight: 1.4, color: 'white', fontFamily: 'Courier New, monospace', fontSize: '0.8rem' }}>
                ID: #2024-XR-7891<br />
                NAME: ALEX CHEN<br />
                SECTOR: ALPHA-7<br />
                CLEARANCE: LEVEL-5<br />
                STATUS: ACTIVE<br /><br />
                <span style={{ color: '#white' }}>→ 翻译各种扫描证件</span>
              </Typography>
              {/* 扫描线效果 */}
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  height: '2px',
                  background: 'linear-gradient(90deg, transparent, #4353ff, transparent)',
                  animation: 'scan 3s infinite'
                }}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      {/* 第二屏及后续内容 */}
      <Box sx={{ 
        py: 4, 
        backgroundColor: 'rgba(228, 228, 228, 0.6)',
        minHeight: '100vh'
      }}>
        <Container maxWidth="xl" sx={{ px: { xs: 2, sm: 3, md: 4 } }}>
          <Box
            sx={{
              backgroundColor: 'transparent',
              borderRadius: '40px',
              boxShadow: 'none',
              overflow: 'hidden',
              p: { xs: 2, sm: 3 },
              height: '100%'
            }}
          >
      {/* 欢迎区域 */}
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography 
          variant="h2" 
          component="h1" 
          sx={{ 
            fontWeight: 700,
            background: 'linear-gradient(90deg, #4353FF 0%, #6B78FF 100%)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            backgroundClip: 'text',
            mb: 2,
            mt: 8
          }}
        >
          欢迎使用 PageSmooth
        </Typography>
        <Typography 
          variant="h6" 
          color="text.secondary" 
          sx={{ mb: 12, maxWidth: 600, mx: 'auto' }}
        >
          提供像素级还原、高质量、认证级、安全的证件翻译服务
        </Typography>
      </Box>

      {/* 支持的文档类型 */}
      <Box sx={{ mb: 8 }}>
        <Typography 
          variant="h3" 
          sx={{
            textAlign: 'center', 
            mb: 8, 
            mt: 8,
            fontWeight: 600,
            color: theme.palette.primary.main,
          }}
        >
          支持文档
        </Typography>
        
        <Grid container spacing={3}>
          {/* 个人证件 */}
          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                height: '100%',
                p: 3,
            borderRadius: 3,
                transition: 'all 0.3s ease',
                border: '2px solid transparent',
            '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 25px rgba(67, 83, 255, 0.15)',
                  border: '2px solid rgba(67, 83, 255, 0.2)'
                }
              }}
            >
              <Typography 
                variant="h6" 
                sx={{ 
                  mb: 2, 
                  fontWeight: 600,
                  color: theme.palette.primary.main,
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <PersonIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                个人证件
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {['身份证', '护照', '出生证明', '工作证', '驾驶证', '机动车登记证', '行驶证', '独生子女证', '退休证', '港澳通行证'].map((doc, index) => (
                  <Box
                    key={index}
                    sx={{
                      px: 1,
                      py: 0.5,
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      borderRadius: 1,
                      fontSize: '0.75rem',
                      color: 'rgba(0, 0, 0, 0.6)',
                      fontWeight: 500
                    }}
                  >
                    {doc}
                  </Box>
                ))}
              </Box>
            </Card>
          </Grid>

          {/* 家庭证件 */}
          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                height: '100%',
                p: 3,
                borderRadius: 3,
                transition: 'all 0.3s ease',
                border: '2px solid transparent',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 25px rgba(67, 83, 255, 0.15)',
                  border: '2px solid rgba(67, 83, 255, 0.2)'
                }
              }}
            >
              <Typography 
                variant="h6" 
                sx={{ 
                  mb: 2, 
                  fontWeight: 600,
                  color: theme.palette.primary.main,
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <SecurityIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                家庭证件
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {['结婚证', '离婚证', '户口本', '房产证'].map((doc, index) => (
                  <Box
                    key={index}
                    sx={{
                      px: 1,
                      py: 0.5,
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      borderRadius: 1,
                      fontSize: '0.75rem',
                      color: 'rgba(0, 0, 0, 0.6)',
                      fontWeight: 500
                    }}
                  >
                    {doc}
                  </Box>
                ))}
              </Box>
            </Card>
          </Grid>

          {/* 学历证书 */}
          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                height: '100%',
                p: 3,
                borderRadius: 3,
                transition: 'all 0.3s ease',
                border: '2px solid transparent',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 25px rgba(67, 83, 255, 0.15)',
                  border: '2px solid rgba(67, 83, 255, 0.2)'
                }
              }}
            >
              <Typography 
                variant="h6" 
                sx={{ 
                  mb: 2, 
                  fontWeight: 600,
                  color: theme.palette.primary.main,
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <SecurityIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                学历证书
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {['毕业证', '学位证', '成绩单', '肄业证', '四六级证书', '学生证', '学籍证明', '学籍卡','教师资格证', '法律职业资格证'].map((doc, index) => (
                  <Box
                    key={index}
                    sx={{
                      px: 1,
                      py: 0.5,
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      borderRadius: 1,
                      fontSize: '0.75rem',
                      color: 'rgba(0, 0, 0, 0.6)',
                      fontWeight: 500
                    }}
                  >
                    {doc}
                  </Box>
                ))}
              </Box>
            </Card>
          </Grid>

          {/* 企业证件 */}
          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                height: '100%',
                p: 3,
                borderRadius: 3,
                transition: 'all 0.3s ease',
                border: '2px solid transparent',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 25px rgba(67, 83, 255, 0.15)',
                  border: '2px solid rgba(67, 83, 255, 0.2)'
                }
              }}
            >
              <Typography 
                variant="h6" 
                sx={{ 
                  mb: 2, 
                  fontWeight: 600,
                  color: theme.palette.primary.main,
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <CloudUploadIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                企业证件
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {['营业执照', '税务登记证', '商标证书', '发明专利证书', '开户许可证'].map((doc, index) => (
                  <Box
                    key={index}
                    sx={{
                      px: 1,
                      py: 0.5,
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      borderRadius: 1,
                      fontSize: '0.75rem',
                      color: 'rgba(0, 0, 0, 0.6)',
                      fontWeight: 500
                    }}
                  >
                    {doc}
                  </Box>
                ))}
              </Box>
            </Card>
          </Grid>

          {/* 流水发票 */}
          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                height: '100%',
                p: 3,
                borderRadius: 3,
                transition: 'all 0.3s ease',
                border: '2px solid transparent',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 25px rgba(67, 83, 255, 0.15)',
                  border: '2px solid rgba(67, 83, 255, 0.2)'
                }
              }}
            >
              <Typography 
                variant="h6" 
                sx={{ 
                  mb: 2, 
                  fontWeight: 600,
                  color: theme.palette.primary.main,
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <SpeedIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                流水发票
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {['银行流水账单', '个人所得税纳税清单'].map((doc, index) => (
                  <Box
                    key={index}
                    sx={{
                      px: 1,
                      py: 0.5,
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      borderRadius: 1,
                      fontSize: '0.75rem',
                      color: 'rgba(0, 0, 0, 0.6)',
                      fontWeight: 500
                    }}
                  >
                    {doc}
                  </Box>
                ))}
              </Box>
            </Card>
          </Grid>

          {/* 其它证明 */}
          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                height: '100%',
                p: 3,
                borderRadius: 3,
                transition: 'all 0.3s ease',
                border: '2px solid transparent',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 25px rgba(67, 83, 255, 0.15)',
                  border: '2px solid rgba(67, 83, 255, 0.2)'
                }
              }}
            >
              <Typography 
                variant="h6" 
                sx={{ 
                  mb: 2, 
                  fontWeight: 600,
                  color: theme.palette.primary.main,
                  display: 'flex',
                  alignItems: 'center'
                }}
              >
                <TranslateIcon sx={{ mr: 1, color: theme.palette.primary.main }} />
                其它证明
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {['在读证明', '实习证明', '在职证明', '工作证明', '存款证明'].map((doc, index) => (
                  <Box
                    key={index}
                    sx={{
                      px: 1,
                      py: 0.5,
                      backgroundColor: 'rgba(0, 0, 0, 0.04)',
                      borderRadius: 1,
                      fontSize: '0.75rem',
                      color: 'rgba(0, 0, 0, 0.6)',
                      fontWeight: 500
                    }}
                  >
                    {doc}
                  </Box>
                ))}
              </Box>
            </Card>
          </Grid>
        </Grid>
      </Box>

      {/* 翻译效果 */}
      <Typography 
        variant="h3" 
        sx={{ 
          textAlign: 'center', 
          mb: 8, 
          mt: 24,
          fontWeight: 600,
          color: theme.palette.primary.main,
        }}
      >
        翻译效果
      </Typography>
      
      <Grid container spacing={6}>
        {/* 第一组对比：营业执照 */}
        <Grid item xs={12} md={4}>
            <Card
              sx={{
              borderRadius: 3,
              overflow: 'hidden',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 12px 35px rgba(67, 83, 255, 0.15)'
              }
            }}
          >
            <Box sx={{ p: 3 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                textAlign: 'center',
                  mb: 3, 
                  fontWeight: 600,
                  color: theme.palette.primary.main 
                }}
              >
                营业执照翻译
              </Typography>
              
              {/* 翻译前 */}
              <Box sx={{ mb: 3 }}>
                <Box
                  sx={{
                    width: '100%',
                    height: '220px',
                    backgroundColor: '#f5f5f5',
                    borderRadius: '16px',
                    border: '1px dashed #ddd',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  {/* 翻译前标签 */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      backgroundColor: 'rgba(0, 0, 0, 0.7)',
                      color: 'white',
                      px: 2,
                      py: 0.5,
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      borderTopLeftRadius: '16px',
                      borderBottomRightRadius: '16px',
                      zIndex: 2
                    }}
                  >
                    翻译前
                  </Box>
                  <img
                    src="/images/yingye3.jpg"
                    alt="中文营业执照原件"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: '16px'
                    }}
                  />
                </Box>
              </Box>
              
              {/* 箭头 */}
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    backgroundColor: theme.palette.primary.main,
                    color: 'white'
                  }}
                >
                  ↓
                </Box>
              </Box>
              
              {/* 翻译后 */}
              <Box>
                <Box
                  sx={{
                    width: '100%',
                    height: '220px',
                    backgroundColor: '#f8f9ff',
                    borderRadius: '16px',
                    border: `1px solid ${theme.palette.primary.main}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  {/* 翻译后标签 */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      backgroundColor: theme.palette.primary.main,
                      color: 'white',
                      px: 2,
                      py: 0.5,
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      borderTopLeftRadius: '12px',
                      borderBottomRightRadius: '16px',
                      zIndex: 2
                    }}
                  >
                    翻译后
                  </Box>
                  <img
                    src="/images/yingye4.jpg"
                    alt="英文营业执照翻译件"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: '16px'
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Card>
        </Grid>

        {/* 第二组对比：成绩单 */}
        <Grid item xs={12} md={4}>
          <Card
            sx={{
                borderRadius: 3,
              overflow: 'hidden',
                transition: 'all 0.3s ease',
                '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 12px 35px rgba(67, 83, 255, 0.15)'
              }
            }}
          >
            <Box sx={{ p: 3 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  textAlign: 'center', 
                  mb: 3, 
                  fontWeight: 600,
                  color: theme.palette.primary.main 
                }}
              >
                成绩单翻译
              </Typography>
              
              {/* 翻译前 */}
              <Box sx={{ mb: 3 }}>
                <Box
                  sx={{
                    width: '100%',
                    height: '220px',
                    backgroundColor: '#f5f5f5',
                    borderRadius: '16px',
                    border: '1px dashed #ddd',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  {/* 翻译前标签 */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      backgroundColor: 'rgba(0, 0, 0, 0.7)',
                      color: 'white',
                      px: 2,
                      py: 0.5,
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      borderTopLeftRadius: '12px',
                      borderBottomRightRadius: '16px',
                      zIndex: 2
                    }}
                  >
                    翻译前
                </Box>
                  <img
                    src="/images/score3.jpg"
                    alt="中文成绩单原件"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: '16px'
                    }}
                  />
                </Box>
              </Box>
              
              {/* 箭头 */}
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    backgroundColor: theme.palette.primary.main,
                    color: 'white'
                  }}
                >
                  ↓
                </Box>
              </Box>
              
              {/* 翻译后 */}
              <Box>
                <Box
                  sx={{
                    width: '100%',
                    height: '220px',
                    backgroundColor: '#f8f9ff',
                    borderRadius: '16px',
                    border: `1px solid ${theme.palette.primary.main}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  {/* 翻译后标签 */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      backgroundColor: theme.palette.primary.main,
                      color: 'white',
                      px: 2,
                      py: 0.5,
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      borderTopLeftRadius: '12px',
                      borderBottomRightRadius: '16px',
                      zIndex: 2
                    }}
                  >
                    翻译后
                  </Box>
                  <img
                    src="/images/score4.jpg"
                    alt="英文成绩单翻译件"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: '16px'
                    }}
                  />
                </Box>
              </Box>
            </Box>
          </Card>
        </Grid>

        {/* 第三组对比：银行流水 */}
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              borderRadius: 3,
              overflow: 'hidden',
              transition: 'all 0.3s ease',
              '&:hover': {
                transform: 'translateY(-4px)',
                boxShadow: '0 12px 35px rgba(67, 83, 255, 0.15)'
              }
            }}
          >
            <Box sx={{ p: 3 }}>
              <Typography 
                variant="h6" 
                sx={{ 
                  textAlign: 'center', 
                  mb: 3, 
                  fontWeight: 600,
                  color: theme.palette.primary.main 
                }}
              >
                银行流水翻译
                </Typography>
              
              {/* 翻译前 */}
              <Box sx={{ mb: 3 }}>
                <Box
                  sx={{
                    width: '100%',
                    height: '220px',
                    backgroundColor: '#f5f5f5',
                    borderRadius: '16px',
                    border: '1px dashed #ddd',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  {/* 翻译前标签 */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      backgroundColor: 'rgba(0, 0, 0, 0.7)',
                      color: 'white',
                      px: 2,
                      py: 0.5,
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      borderTopLeftRadius: '12px',
                      borderBottomRightRadius: '16px',
                      zIndex: 2
                    }}
                  >
                    翻译前
                  </Box>
                  <img
                    src="/images/bank3.jpg"
                    alt="中文银行流水原件"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: '16px'
                    }}
                  />
                </Box>
              </Box>
              
              {/* 箭头 */}
              <Box sx={{ textAlign: 'center', mb: 3 }}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 40,
                    height: 40,
                    borderRadius: '50%',
                    backgroundColor: theme.palette.primary.main,
                    color: 'white'
                  }}
                >
                  ↓
                </Box>
              </Box>
              
              {/* 翻译后 */}
              <Box>
                <Box
                  sx={{
                    width: '100%',
                    height: '220px',
                    backgroundColor: '#f8f9ff',
                    borderRadius: '16px',
                    border: `1px solid ${theme.palette.primary.main}`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  {/* 翻译后标签 */}
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      backgroundColor: theme.palette.primary.main,
                      color: 'white',
                      px: 2,
                      py: 0.5,
                      fontSize: '0.75rem',
                      fontWeight: 500,
                      borderTopLeftRadius: '12px',
                      borderBottomRightRadius: '16px',
                      zIndex: 2
                    }}
                  >
                    翻译后
                  </Box>
                  <img
                    src="/images/bank4.jpg"
                    alt="英文银行流水翻译件"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: '16px'
                    }}
                  />
                </Box>
              </Box>
            </Box>
            </Card>
          </Grid>
      </Grid>

      {/* 功能特性 */}
      <Typography 
        variant="h3" 
        sx={{ 
          textAlign: 'center', 
          mb: 8, 
          mt: 24,
          fontWeight: 600,
          color: theme.palette.primary.main,
        }}
      >
        功能特性
      </Typography>
      
      <Grid container spacing={4}>
        {features.map((feature, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card
              sx={{
                height: '100%',
                textAlign: 'center',
                p: 3,
                borderRadius: 3,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-8px)',
                  boxShadow: '0 12px 35px rgba(0, 0, 0, 0.1)'
                }
              }}
            >
              <CardContent>
                <Box sx={{ mb: 2 }}>
                  {feature.icon}
                </Box>
                <Typography variant="h6" component="h3" sx={{ mb: 2, fontWeight: 600 }}>
                  {feature.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {feature.description}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* 他们都在用 - 用户评价轮播 */}
      <Box sx={{ mt: 24, mb: 8 }}>
        <Typography 
          variant="h3" 
          sx={{ 
            textAlign: 'center', 
            mb: 6, 
            fontWeight: 600,
            color: theme.palette.primary.main,
          }}
        >
          他们都在用
        </Typography>
        
        <Box
          sx={{
            position: 'relative',
            overflow: 'hidden',
            height: '320px',
            display: 'flex',
            alignItems: 'center',
            '&:hover .testimonial-track': {
              animationPlayState: 'paused'
            }
          }}
        >
          <Box
            className="testimonial-track"
            sx={{
              display: 'flex',
              gap: 3,
              animation: 'scroll 45s linear infinite',
              width: 'fit-content',
              '@keyframes scroll': {
                '0%': { transform: 'translateX(0)' },
                '100%': { transform: 'translateX(-50%)' }
              }
            }}
          >
            {/* 第一组评价卡片 */}
            {[
              {
                type: '留学机构',
                name: '启德教育',
                title: '专业留学咨询机构',
                content: '我们为学生处理各种留学文件翻译，PageSmooth的翻译质量非常专业，格式保持完美，大大提高了我们的工作效率。',
                rating: 5
              },
              {
                type: '个人',
                name: '张同学',
                title: '澳洲留学生',
                content: '申请签证需要翻译很多材料，包括还需要NAATI认证，PageSmooth帮我完成了成绩单和毕业证的翻译，翻译很赞！',
                rating: 5
              },
              {
                type: '淘宝商家',
                name: '海外代购小店',
                title: '跨境电商',
                content: '经常需要翻译产品说明书和海关文件，PageSmooth让我们的业务更加顺畅，客户满意度大幅提升。',
                rating: 5
              },
              {
                type: '留学机构',
                name: '起航远途',
                title: '澳洲留学服务机构',
                content: '作为留学服务机构，我们对翻译质量要求比较高。PageSmooth的认证翻译服务完全满足我们的需求，服务一对一很很赞。',
                rating: 5
              },
              {
                type: '个人',
                name: '李女士',
                title: '移民申请人',
                content: '移民材料翻译要求很严格，PageSmooth提供的认证翻译让我顺利通过了审核，非常感谢！',
                rating: 5
              },
              {
                type: '个人',
                name: '王先生',
                title: '企业高管',
                content: '出国工作需要翻译各种证件，PageSmooth的服务快速准确，让我的签证申请过程很顺利。',
                rating: 5
              },
              {
                type: '淘宝商家',
                name: '全球购精品店',
                title: '进口商品零售',
                content: '进口商品的各种证书翻译都交给PageSmooth，专业可靠，是我们信赖的合作伙伴。',
                rating: 5
              },
              {
                type: '留学机构',
                name: '留学小站',
                title: '留学服务专家',
                content: '我们推荐PageSmooth给所有需要文件翻译的学生，质量有保障，价格也很合理。',
                rating: 5
              },
              {
                type: '个人',
                name: '陈博士',
                title: '海外学者',
                content: '学术论文和研究资料的翻译对准确性要求极高，PageSmooth从未让我失望过。',
                rating: 5
              },
              {
                type: '个人',
                name: '刘小姐',
                title: '旅游爱好者',
                content: '出国旅游前翻译了驾照和各种证件，PageSmooth让我的旅行更加安心。',
                rating: 4
              }
            ].concat([
              // 重复一遍数据以实现无缝循环
              {
                type: '翻译机构',
                name: '译悦翻译',
                title: '专业翻译机构',
                content: '我们为学生处理各种留学文件翻译，PageSmooth的翻译质量非常专业，格式保持完美，大大提高了我们的工作效率。',
                rating: 5
              },
              {
                type: '个人',
                name: '杨同学',
                title: '澳洲留学生',
                content: '申请签证需要翻译很多材料，PageSmooth帮我快速完成了成绩单和毕业证的翻译，翻译质量很棒！',
                rating: 5
              },
              {
                type: '淘宝商家',
                name: '海外代购小店',
                title: '跨境电商',
                content: '经常需要翻译产品说明书和海关文件，PageSmooth让我们的业务更加顺畅，客户满意度大幅提升。',
                rating: 5
              },
              {
                type: '留学机构',
                name: '远洋留学',
                title: '留学教育机构',
                content: 'PageSmooth的认证翻译服务完全满足我们的需求，服务很专业。',
                rating: 5
              },
              {
                type: '个人',
                name: '王女士',
                title: '移民申请人',
                content: '移民材料翻译要求很严格，PageSmooth提供的认证翻译让我顺利通过了审核，非常感谢！',
                rating: 5
              },
              {
                type: '个人',
                name: '王先生',
                title: '企业高管',
                content: '出国工作需要翻译各种证件，PageSmooth的服务快速准确，让我的签证申请过程很顺利。',
                rating: 5
              },
              {
                type: '淘宝商家',
                name: '全球购精品店',
                title: '进口商品零售',
                content: '进口商品的各种证书翻译都交给PageSmooth，专业可靠，是我们信赖的合作伙伴。',
                rating: 5
              },
              {
                type: '留学机构',
                name: '津桥留学',
                title: '留学服务专家',
                content: '我们推荐PageSmooth给所有需要文件翻译的学生，质量有保障，价格也很合理。',
                rating: 5
              },
              {
                type: '个人',
                name: '陈博士',
                title: '海外学者',
                content: '学术论文和研究资料的翻译对准确性要求极高，PageSmooth从未让我失望过。',
                rating: 5
              },
              {
                type: '个人',
                name: '刘小姐',
                title: '旅游爱好者',
                content: '出国旅游前翻译了驾照和各种证件，PageSmooth让我的旅行更加安心。',
                rating: 4
              }
            ]).map((testimonial, index) => (
              <Card
                key={index}
                sx={{
                  minWidth: '350px',
                  maxWidth: '350px',
                  p: 3,
                  borderRadius: 3,
                  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.08)',
                  border: '1px solid rgba(0, 0, 0, 0.06)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-4px)',
                    boxShadow: '0 8px 30px rgba(67, 83, 255, 0.15)'
                  }
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  {/* 头像占位符 */}
                  <Box
                    sx={{
                      width: 48,
                      height: 48,
                      borderRadius: '50%',
                      backgroundColor: 'rgba(0, 0, 0, 0.1)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mr: 2
                    }}
                  >
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path
                        d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"
                        fill="rgba(0, 0, 0, 0.3)"
                      />
                    </svg>
                  </Box>
                  
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 0.5 }}>
                      {testimonial.name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: 'text.secondary',
                          fontSize: '0.75rem'
                        }}
                      >
                        {testimonial.title}
                      </Typography>
                      <Box
                        sx={{
                          px: 1,
                          py: 0.25,
                          backgroundColor: testimonial.type === '留学机构' 
                            ? 'rgba(67, 83, 255, 0.1)' 
                            : testimonial.type === '淘宝商家'
                            ? 'rgba(255, 152, 0, 0.1)'
                            : 'rgba(76, 175, 80, 0.1)',
                          borderRadius: 1,
                          fontSize: '0.7rem',
                          color: testimonial.type === '留学机构' 
                            ? theme.palette.primary.main
                            : testimonial.type === '淘宝商家'
                            ? '#ff9800'
                            : '#4caf50',
                          fontWeight: 500
                        }}
                      >
                        {testimonial.type}
                      </Box>
                    </Box>
                  </Box>
                </Box>
                
                <Typography 
                  variant="body2" 
                  sx={{ 
                    lineHeight: 1.6,
                    color: 'text.secondary',
                    mb: 2,
                    fontSize: '0.875rem'
                  }}
                >
                  "{testimonial.content}"
                </Typography>
                
                {/* 星级评分 */}
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  {[...Array(5)].map((_, starIndex) => (
                    <Box
                      key={starIndex}
                      sx={{
                        color: starIndex < testimonial.rating 
                          ? '#ffc107' 
                          : 'rgba(0, 0, 0, 0.1)',
                        fontSize: '1rem',
                        mr: 0.25
                      }}
                    >
                      ★
                    </Box>
                  ))}
                  <Typography 
                    variant="caption" 
                    sx={{ 
                      ml: 1, 
                      color: 'text.secondary',
                      fontSize: '0.75rem'
                    }}
                  >
                    {testimonial.rating}/5
                  </Typography>
                </Box>
              </Card>
            ))}
          </Box>
        </Box>
      </Box>

      {/* 快速操作区域 */}
      <Box sx={{ mt: 24,mb: 8, textAlign: 'center' }}>
        <Typography variant="h3" sx={{ mb: 8, fontWeight: 600, color: theme.palette.primary.main }}>
          快速开始
        </Typography>
        <Grid container spacing={3} justifyContent="center">
          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                p: 3,
                cursor: 'pointer',
                borderRadius: 3,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 25px rgba(67, 83, 255, 0.15)'
                }
              }}
              onClick={() => {
                navigate('/trans');
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              <Typography variant="h6" sx={{ mb: 1, color: theme.palette.primary.main }}>
                上传文档
              </Typography>
              <Typography variant="body2" color="text.secondary">
                立即上传您的文档开始翻译
              </Typography>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <Card
              sx={{
                p: 3,
                cursor: 'pointer',
                borderRadius: 3,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 25px rgba(67, 83, 255, 0.15)'
                }
              }}
              onClick={() => {
                navigate('/pricing');
                window.scrollTo({ top: 0, behavior: 'smooth' });
              }}
            >
              <Typography variant="h6" sx={{ mb: 1, color: theme.palette.primary.main }}>
                查看定价
              </Typography>
              <Typography variant="body2" color="text.secondary">
                了解我们的服务套餐和价格
              </Typography>
            </Card>
          </Grid>
        </Grid>
      </Box>
          </Box>
        </Container>
      </Box>
      
      {/* 底部Footer */}
      <AppFooter />

      {/* Login Modal */}
      <LoginModal
        open={showLoginModal}
        onClose={() => setShowLoginModal(false)}
        onLoginSuccess={handleLoginSuccess}
      />

      {/* Invite Code Modal */}
      <InviteCodeModal
        open={showInviteModal}
        onClose={() => setShowInviteModal(false)}
        onSuccess={handleInviteSuccess}
      />
    </Box>
  );
};

export default Landing; 